/* Gents Nest theme — shared behaviour. Vanilla web components, no dependencies. */

const GN = {
  debounce(fn, wait = 300) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
  },
  fetchConfig(type = 'json') {
    return { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: `application/${type}` } };
  },
  formatMoney(cents, format = (window.theme && window.theme.moneyFormat) || 'KES {{amount}}') {
    const value = Number(cents) / 100;
    const withDelimiters = (n, decimals, thousands = ',', dec = '.') => {
      const parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(dec);
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0);
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        default: return withDelimiters(value, 2);
      }
    });
  },
  lockScroll(lock) { document.body.classList.toggle('is-locked', lock); },
  trapFocus(container) {
    const focusables = container.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusables.length) focusables[0].focus();
  }
};
window.GN = GN;

/* ---------- drawers: menu, filters, size guide (cart drawer extends this in cart.js) ---------- */
class GnDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = document.querySelector('[data-overlay]');
    this.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.close(); });
    this.querySelectorAll('[data-drawer-close]').forEach((b) => b.addEventListener('click', () => this.close()));
  }
  open(opener) {
    this.opener = opener || document.activeElement;
    document.querySelectorAll('gn-drawer.is-open, cart-drawer.is-open').forEach((d) => d !== this && d.close && d.close(true));
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    if (this.overlay) {
      this.overlay.hidden = false;
      this.overlay.onclick = () => this.close();
    }
    GN.lockScroll(true);
    setTimeout(() => GN.trapFocus(this), 50);
  }
  close(silent) {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    if (this.overlay && !silent) this.overlay.hidden = true;
    GN.lockScroll(false);
    if (this.opener && !silent) this.opener.focus();
  }
}
customElements.define('gn-drawer', GnDrawer);

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-drawer-open]');
  if (!trigger) return;
  const drawer = document.getElementById(trigger.dataset.drawerOpen);
  if (drawer && drawer.open) { e.preventDefault(); drawer.open(trigger); }
});

/* ---------- announcement bar rotation ---------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.messages = [...this.querySelectorAll('.announcement__message')];
    if (this.messages.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.index = 0;
    const speed = Number(this.dataset.speed || 5) * 1000;
    this.timer = setInterval(() => this.next(), speed);
    this.addEventListener('mouseenter', () => clearInterval(this.timer));
    this.addEventListener('mouseleave', () => { this.timer = setInterval(() => this.next(), speed); });
  }
  next() {
    this.messages[this.index].classList.remove('is-active');
    this.messages[this.index].setAttribute('aria-hidden', 'true');
    this.index = (this.index + 1) % this.messages.length;
    this.messages[this.index].classList.add('is-active');
    this.messages[this.index].setAttribute('aria-hidden', 'false');
  }
  disconnectedCallback() { clearInterval(this.timer); }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ---------- desktop mega menu ---------- */
class HeaderMenu extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-mega]')];
    this.items.forEach((item) => {
      const btn = item.querySelector('[data-mega-toggle]');
      let closeTimer;
      const open = () => { clearTimeout(closeTimer); this.closeAll(item); item.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); };
      const close = () => { item.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false'); };
      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', () => { closeTimer = setTimeout(close, 120); });
      btn.addEventListener('click', (e) => { e.preventDefault(); item.classList.contains('is-open') ? close() : open(); });
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
      item.addEventListener('keyup', (e) => { if (e.key === 'Escape') { close(); btn.focus(); } });
    });
  }
  closeAll(except) {
    this.items.forEach((i) => { if (i !== except) { i.classList.remove('is-open'); i.querySelector('[data-mega-toggle]').setAttribute('aria-expanded', 'false'); } });
  }
}
customElements.define('header-menu', HeaderMenu);

/* sticky header shadow */
(() => {
  const header = document.querySelector('.site-header.is-sticky');
  if (!header) return;
  const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
  window.addEventListener('scroll', onScroll, { passive: true });
})();

/* ---------- quantity input ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('button[name]').forEach((b) => b.addEventListener('click', (e) => {
      e.preventDefault();
      const prev = this.input.value;
      b.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
      if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }));
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- tabs (size guide categories, account) ---------- */
class GnTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.select((i + 1) % this.tabs.length, true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.select((i - 1 + this.tabs.length) % this.tabs.length, true); }
      });
    });
  }
  select(index, focus) {
    this.tabs.forEach((t, i) => { t.setAttribute('aria-selected', i === index); t.tabIndex = i === index ? 0 : -1; });
    this.panels.forEach((p, i) => { p.hidden = i !== index; });
    if (focus) this.tabs[index].focus();
  }
}
customElements.define('gn-tabs', GnTabs);

/* ---------- product: variant picker + gallery + add to cart ---------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('[data-section-id]');
    this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);
    this.addEventListener('change', () => this.onChange());
    const current = this.variants.find((v) => String(v.id) === String((this.section.querySelector('input[name="id"]') || {}).value));
    if (current) this.renderStock(current);
  }
  selectedOptions() {
    return [...this.querySelectorAll('fieldset')].map((fs) => {
      const checked = fs.querySelector('input:checked');
      return checked ? checked.value : null;
    });
  }
  onChange() {
    const options = this.selectedOptions();
    const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
    this.updateAvailability(options);
    this.querySelectorAll('[data-selected-value]').forEach((el, i) => { el.textContent = options[i] || ''; });
    const form = this.section.querySelector('product-form');
    if (!variant) { form && form.setUnavailable(); return; }
    this.section.querySelectorAll('input[name="id"]').forEach((i) => { i.value = variant.id; i.dispatchEvent(new Event('change', { bubbles: true })); });
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
    this.renderPrice(variant);
    form && form.setVariant(variant);
    this.renderStock(variant);
    if (variant.featured_media) {
      const gallery = this.section.querySelector('product-gallery');
      gallery && gallery.show(variant.featured_media.id);
    }
  }
  updateAvailability(options) {
    const fieldsets = [...this.querySelectorAll('fieldset')];
    fieldsets.forEach((fs, index) => {
      fs.querySelectorAll('input').forEach((input) => {
        const candidate = [...options];
        candidate[index] = input.value;
        const available = this.variants.some((v) => v.available && v.options.every((o, i) => i === index ? o === input.value : (i < index ? o === candidate[i] : true)));
        input.closest('.swatch').classList.toggle('is-unavailable', !available);
      });
    });
  }
  stockLevels() {
    if (this._stock !== undefined) return this._stock;
    const el = this.querySelector('[data-stock-levels]');
    this._stock = el ? { levels: JSON.parse(el.textContent), threshold: Number(el.dataset.threshold) || 3 } : null;
    return this._stock;
  }
  renderStock(variant) {
    const msg = this.querySelector('[data-stock-message]');
    const stock = this.stockLevels();
    if (!msg || !stock) return;
    const form = this.section.querySelector('product-form');
    const sizeIndex = form ? Number(form.dataset.sizeIndex) : -1;
    const prefix = form ? form.dataset.sizePrefix || '' : '';
    // Mark sizes that are running low (for the other options currently selected)
    const options = this.selectedOptions();
    const sizeFs = sizeIndex >= 0 ? this.querySelectorAll('fieldset')[sizeIndex] : null;
    if (sizeFs) sizeFs.querySelectorAll('input').forEach((input) => {
      const v = this.variants.find((x) => x.options.every((o, i) => (i === sizeIndex ? o === input.value : o === options[i])));
      const q = v ? stock.levels[v.id] : undefined;
      input.closest('.swatch').classList.toggle('is-low', q !== undefined && q > 0 && q <= stock.threshold);
    });
    const qty = variant ? stock.levels[variant.id] : undefined;
    if (qty === undefined || qty <= 0 || qty > stock.threshold) { msg.hidden = true; return; }
    const sizeVal = sizeIndex >= 0 ? variant.options[sizeIndex] : '';
    const size = sizeVal ? `${/\d/.test(sizeVal) ? prefix : ''} ${sizeVal}`.trim() : '';
    const tpl = qty === 1 ? window.theme.strings.lowStockOne : window.theme.strings.lowStock;
    msg.innerHTML = `<span class="product__stock-dot" aria-hidden="true"></span><span>${tpl.replace('[count]', qty).replace('[size]', `<strong>${size}</strong>`)}</span>`;
    msg.hidden = false;
  }
  renderPrice(variant) {
    const price = this.section.querySelector('[data-product-price]');
    if (!price) return;
    const onSale = variant.compare_at_price > variant.price;
    price.classList.toggle('price--sale', onSale);
    price.innerHTML = `<span class="price__current">${GN.formatMoney(variant.price)}</span>${onSale ? `<s class="price__compare">${GN.formatMoney(variant.compare_at_price)}</s>` : ''}`;
  }
}
customElements.define('variant-picker', VariantPicker);

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-gallery-main]');
    this.querySelectorAll('[data-thumb]').forEach((t) => t.addEventListener('click', () => this.show(t.dataset.thumb)));
    // Phones: swipeable slides with dots and an 'n / total' counter
    this.dots = [...this.querySelectorAll('.gallery__dot')];
    this.counter = this.querySelector('[data-gallery-index]');
    if (this.dots.length) this.main.addEventListener('scroll', () => {
      const slides = [...this.main.children];
      const w = this.main.clientWidth;
      const i = Math.min(slides.length - 1, Math.max(0, Math.round(this.main.scrollLeft / (slides[0].offsetWidth || w))));
      this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      if (this.counter) this.counter.textContent = i + 1;
    }, { passive: true });
  }
  show(mediaId) {
    const slide = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;
    this.querySelectorAll('[data-media-id]').forEach((s) => s.classList.toggle('is-active', s === slide));
    this.querySelectorAll('[data-thumb]').forEach((t) => t.setAttribute('aria-current', t.dataset.thumb == mediaId));
    if (window.matchMedia('(max-width: 989px)').matches) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }
}
customElements.define('product-gallery', ProductGallery);

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[type="submit"]');
    this.error = this.querySelector('[data-form-error]');
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.watchSticky();
  }
  setVariant(variant) {
    this.button.disabled = !variant.available;
    this.label().textContent = variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
    const sizeIndex = Number(this.dataset.sizeIndex);
    const sizeVal = sizeIndex >= 0 ? variant.options[sizeIndex] : '';
    const size = sizeVal ? `${/\d/.test(sizeVal) ? this.dataset.sizePrefix || '' : ''} ${sizeVal}`.trim() : '';
    const summary = `${size ? `${size} · ` : ''}${GN.formatMoney(variant.price)}`;
    const meta = this.querySelector('[data-add-meta]');
    if (meta) meta.innerHTML = variant.available ? `&nbsp;— ${summary}` : '';
    const stickyMeta = this.querySelector('[data-sticky-meta]');
    if (stickyMeta) stickyMeta.textContent = summary;
    const stickyBtn = this.querySelector('.product__sticky-add');
    if (stickyBtn) stickyBtn.disabled = !variant.available;
  }
  setUnavailable() {
    this.button.disabled = true;
    this.label().textContent = window.theme.strings.unavailable;
    const meta = this.querySelector('[data-add-meta]');
    if (meta) meta.innerHTML = '';
    const stickyBtn = this.querySelector('.product__sticky-add');
    if (stickyBtn) stickyBtn.disabled = true;
  }
  label() { return this.button.querySelector('[data-add-label]') || this.button.querySelector('span'); }
  watchSticky() {
    const bar = this.querySelector('[data-sticky-bar]');
    if (!bar || !('IntersectionObserver' in window)) return;
    // Show the bar once the main Add to bag button has scrolled out of view (phones only, via CSS)
    new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.querySelector('button').tabIndex = show ? 0 : -1;
    }).observe(this.button);
  }
  async onSubmit(e) {
    const cart = document.querySelector('cart-drawer');
    if (!cart || window.theme.cartType !== 'drawer') return; // normal form post to /cart
    e.preventDefault();
    if (this.button.disabled) return;
    this.button.classList.add('is-loading');
    this.error.hidden = true;
    const body = new FormData(this.form);
    body.append('sections', cart.sectionIds().join(','));
    body.append('sections_url', window.location.pathname);
    try {
      const res = await fetch(`${window.routes.cart_add_url}`, { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body });
      const data = await res.json();
      if (data.status) {
        this.error.textContent = data.description || data.message || window.theme.strings.cartError;
        this.error.hidden = false;
        return;
      }
      cart.renderSections(data.sections);
      cart.open(this.button);
    } catch (err) {
      this.error.textContent = window.theme.strings.cartError;
      this.error.hidden = false;
    } finally {
      this.button.classList.remove('is-loading');
    }
  }
}
customElements.define('product-form', ProductForm);

/* ---------- collection / search filters (Section Rendering API) ---------- */
class FacetForm extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    const selector = `form[data-facet-form="${this.sectionId}"]`;
    const onChange = GN.debounce((form) => this.submit(form), 450);
    // delegated: the filter forms are replaced after every update
    document.addEventListener('input', (e) => { const f = e.target.closest(selector); if (f) onChange(f); });
    document.addEventListener('submit', (e) => { const f = e.target.closest(selector); if (f) { e.preventDefault(); this.submit(f); } });
    document.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-facet-remove]');
      if (!remove || (!this.contains(remove) && !remove.closest(`[data-facet-scope="${this.sectionId}"], #FacetDrawer-${this.sectionId}`))) return;
      e.preventDefault();
      this.render(new URL(remove.href, window.location.origin).searchParams.toString());
    });
    window.addEventListener('popstate', (e) => this.render(e.state ? e.state.params : new URL(window.location).searchParams.toString(), false));
  }
  submit(form) {
    const params = new URLSearchParams(new FormData(form));
    for (const [k, v] of [...params.entries()]) if (v === '') params.delete(k);
    // keep sort order and search terms shared between the toolbar, desktop and mobile forms
    document.querySelectorAll(`form[data-facet-form="${this.sectionId}"]`).forEach((f) => {
      if (f !== form) f.querySelectorAll('[data-shared-param]').forEach((i) => { if (!params.has(i.name) && i.value) params.set(i.name, i.value); });
    });
    this.render(params.toString());
  }
  async render(params, push = true) {
    const results = document.getElementById(`ResultsWrapper-${this.sectionId}`);
    results && results.classList.add('is-loading');
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${params}`;
    const html = await fetch(url).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    ['ResultsWrapper', 'FacetsDesktop', 'FacetsMobile', 'ActiveFacets', 'ResultsCount'].forEach((key) => {
      const target = document.getElementById(`${key}-${this.sectionId}`);
      const source = doc.getElementById(`${key}-${this.sectionId}`);
      if (target && source) { target.innerHTML = source.innerHTML; target.hidden = source.hidden; }
    });
    results && results.classList.remove('is-loading');
    if (push) history.pushState({ params }, '', `${window.location.pathname}${params ? `?${params}` : ''}`);
  }
}
customElements.define('facet-form', FacetForm);

/* price range: keep min below max */
document.addEventListener('change', (e) => {
  const input = e.target.closest('[data-price-range] input');
  if (!input) return;
  const wrap = input.closest('[data-price-range]');
  const [min, max] = wrap.querySelectorAll('input');
  if (min.value && max.value && Number(min.value) > Number(max.value)) [min.value, max.value] = [max.value, min.value];
});

/* ---------- FAQ search: filter questions as you type ---------- */
class FaqSearch extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input');
    const scope = document.getElementById(this.dataset.target);
    if (!input || !scope) return;
    input.addEventListener('input', () => {
      const q = input.value.trim().toLowerCase();
      scope.querySelectorAll('[data-faq-item]').forEach((item) => { item.hidden = q && !item.textContent.toLowerCase().includes(q); });
      scope.querySelectorAll('[data-faq-group]').forEach((g) => { g.hidden = ![...g.querySelectorAll('[data-faq-item]')].some((i) => !i.hidden); });
      const empty = scope.querySelector('[data-faq-empty]');
      if (empty) empty.hidden = [...scope.querySelectorAll('[data-faq-item]')].some((i) => !i.hidden);
    });
  }
}
customElements.define('faq-search', FaqSearch);

/* ---------- copy to clipboard (gift card, share) ---------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-copy]');
  if (!btn) return;
  try {
    await navigator.clipboard.writeText(btn.dataset.copy);
    const label = btn.querySelector('[data-copy-label]');
    if (label) { const prev = label.textContent; label.textContent = btn.dataset.copiedText || 'Copied'; setTimeout(() => { label.textContent = prev; }, 2000); }
  } catch (err) { /* clipboard blocked: leave the code visible to copy by hand */ }
});

/* ---------- share ---------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-share]');
  if (!btn || !navigator.share) return;
  e.preventDefault();
  try { await navigator.share({ title: btn.dataset.title, url: btn.dataset.url }); } catch (err) { /* dismissed */ }
});

/* ---------- product recommendations: loaded after the page ---------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.dataset.loaded) return;
    const load = async () => {
      this.dataset.loaded = 'true';
      try {
        const html = await fetch(this.dataset.url).then((r) => r.text());
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
        if (fresh && fresh.querySelector('.product-grid li')) this.innerHTML = fresh.innerHTML;
        else if (!this.querySelector('.product-grid li')) this.remove();
      } catch (err) { if (!this.querySelector('.product-grid li')) this.remove(); }
    };
    if (!('IntersectionObserver' in window)) { load(); return; }
    new IntersectionObserver((entries, obs) => {
      if (entries[0].isIntersecting) { obs.disconnect(); load(); }
    }, { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}
customElements.define('product-recommendations', ProductRecommendations);

/* ---------- quick add from product cards ---------- */
class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-variant]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      this.add(btn);
    });
  }
  async add(btn) {
    const cart = document.querySelector('cart-drawer');
    btn.classList.add('is-loading');
    const body = { items: [{ id: Number(btn.dataset.quickVariant), quantity: 1 }] };
    if (cart) { body.sections = cart.sectionIds(); body.sections_url = window.location.pathname; }
    try {
      const res = await fetch(window.routes.cart_add_url, { ...GN.fetchConfig(), body: JSON.stringify(body) });
      const data = await res.json();
      if (data.status) { alert(data.description || window.theme.strings.cartError); return; }
      const details = this.querySelector('details');
      if (details) details.open = false;
      if (cart) { cart.renderSections(data.sections); cart.open(btn); } else { window.location.href = window.routes.cart_url; }
    } catch (err) {
      alert(window.theme.strings.cartError);
    } finally {
      btn.classList.remove('is-loading');
    }
  }
}
customElements.define('quick-add', QuickAdd);

/* Scroll-snap carousel: arrows page by the number of visible slides and disable at either end. */
class GnCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    if (!this.track) return;
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    window.addEventListener('resize', () => this.update());
    this.update();
  }
  go(dir) {
    this.track.scrollBy({ left: dir * this.track.clientWidth, behavior: 'smooth' });
  }
  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = this.track.scrollLeft;
    if (this.prev) this.prev.disabled = x <= 2;
    if (this.next) this.next.disabled = x >= max - 2;
    this.classList.toggle('is-static', max <= 2);
  }
}
customElements.define('gn-carousel', GnCarousel);
